import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { ReactComponent as CopyLinkIcon } from 'assets/icons/share_link.svg';
import { COPY_SHARE_LINK } from 'constants/mixpanel';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { UserType, UserTypes } from 'modules/User';
import {
  ClickEventTracker,
  EventPropsBool,
  ShareMethod,
} from 'modules/common/components/ClickEventHandler/ClickEventWrapper';
import { useToast } from 'modules/common/components/Toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { isStandalone } from 'utils/environment';

import { HandleClickUpgrade } from './DownloadModal/DownloadModal.types';
import { OptionTitle } from './DownloadModal/OptionTitle';

interface SharePanelProps {
  userType: UserType | undefined;
  handleClickUpgrade?: HandleClickUpgrade;
}

/**
 *
 * @property {UserType} userType - user type
 * @return React component
 * @deprecated This component will be removed when we provide 'enable_editor2_teamshare' feature
 */
const SharePanel = ({ userType, handleClickUpgrade }: SharePanelProps) => {
  const { id } = useParams();
  const { t } = useTranslation('common_navbar');
  const { venngageE2 } = useVenngageE1Gate();

  const sharePanelTestId = 'share-panel';

  const isFreeUser = userType === UserTypes.Free;

  const toast = useToast();

  const showCopySuccessToast = () => {
    toast({ title: 'Link copied!' });
  };

  const showCopyFailedToast = (description?: string) => {
    const descriptionObj = description ? { description } : null;
    toast({
      title: 'Failed to copy link',
      status: 'error',
      ...descriptionObj,
    });
  };

  const handleClick = () => {
    const shareLinkId = isStandalone ? id : venngageE2?.privateLinkId;
    if (!!shareLinkId) {
      const plPageUrl = `${window.location.host}/pl/${shareLinkId}`;
      navigator.clipboard.writeText(plPageUrl).then(
        () => {
          showCopySuccessToast();
        },
        () => {
          showCopyFailedToast();
        },
      );
    } else {
      showCopyFailedToast('share link is not available');
    }
  };

  return (
    <Flex flexDir={'column'} mt='4' mb='30' w={'full'} data-testid={sharePanelTestId}>
      <OptionTitle
        upgradeDescription={t('modals.download.upgrade-premium')}
        shouldUpgrade={isFreeUser}
        handleClickUpgrade={handleClickUpgrade}
      >
        {t('modals.share-panel.link-share')}
      </OptionTitle>
      <Flex flexDir='row' justifyContent='space-between' alignItems='center' w={'full'}>
        <Box w={'full'}>
          <Text fontSize='xs'>{t('modals.share-panel.accessibility-text')}</Text>
          <Text fontSize='xs'>{t('modals.share-panel.view-only-text')}</Text>
        </Box>
        <ClickEventTracker
          eventProps={{
            name: COPY_SHARE_LINK.eventName,
            design_id: id,
            share_method: ShareMethod.PrivateLink,
            is_design_owner: EventPropsBool.true,
          }}
        >
          <Button
            onClick={handleClick}
            isDisabled={isFreeUser}
            variant='action'
            h='8'
            px='3'
            py='1.5'
            mb='3'
            leftIcon={<CopyLinkIcon style={{ marginRight: 1 }} />}
            minWidth={'auto'}
            _hover={{
              bg: 'action-green.700',
              _disabled: {
                bg: 'action-green.500',
              },
            }}
          >
            {t('modals.share-panel.copy-link')}
          </Button>
        </ClickEventTracker>
      </Flex>
    </Flex>
  );
};

export default SharePanel;
